import { decode } from 'js-base64';
import { createContext, ReactNode, useEffect, useRef, useState } from 'react';
import ringCentralApi from '~/apis/ring-central';
import { ringCentralEmbeddableUrl, ringCentralServer } from '~/utils/constants/env';
import {
  RING_CENTRAL_TYPE_ACTIVE_CALL_NOTIFY,
  RING_CENTRAL_TYPE_AUTHORIZATION_CODE_ADAPTER,
  RING_CENTRAL_TYPE_CALL_END_NOTIFY,
  RING_CENTRAL_TYPE_CONNECTED_STATUS,
  RING_CENTRAL_TYPE_CONNECTING_STATUS,
  RING_CENTRAL_TYPE_CONNECTION_STATUS_NOTIFY,
  RING_CENTRAL_TYPE_DISCONNECTED_STATUS,
  RING_CENTRAL_TYPE_LOGIN_STATUS_NOTIFY,
  RING_CENTRAL_TYPE_LOGOUT_ADAPTER,
  RING_CENTRAL_TYPE_NATIVE_AUTHORIZE,
  RING_CENTRAL_TYPE_NATIVE_DISCONNECT,
  RING_CENTRAL_TYPE_NATIVE_PHONE_CALL,
  RING_CENTRAL_TYPE_NATIVE_SEND_CREDENTIAL,
  RING_CENTRAL_TYPE_NATIVE_SMS,
  RING_CENTRAL_TYPE_NEW_CALL_ADAPTER,
  RING_CENTRAL_TYPE_NEW_SMS_ADAPTER,
  RING_CENTRAL_TYPE_RINGING_TELEPHONY_STATUS,
  RING_CENTRAL_TYPE_WIDGET_FRAME_ADAPTER,
} from '~/utils/constants/ring-central';
import { ConnectionStatusEnum, StorageEnum } from '~/utils/enum';
import { catchError, notify } from '~/utils/helper';

type RingCentralContextType = {
  excuteCall?: (phoneNumber: string) => void;
  sendSMS?: (phoneNumber: string) => void;
  connect?: () => void;
  disconnect?: () => void;
  ringCentralStatus: ConnectionStatusEnum;
  setIsJoinPage?: (isLogin: boolean) => void;
  setIsShowWidget?: (isShowWidget: boolean) => void;
  getAuthUrl?: (clientId: string, redirectUri: string) => string;
  authorize?: (code: string) => void;
};

const RingCentralContext = createContext<RingCentralContextType>({
  ringCentralStatus: ConnectionStatusEnum.DISCONNECTED,
});

type RingCentralProviderProps = {
  children: ReactNode;
};

const RingCentralProvider = ({ children }: RingCentralProviderProps) => {
  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [ringCentralStatus, setRingCentralStatus] = useState<ConnectionStatusEnum>(ConnectionStatusEnum.DISCONNECTED);
  const [isJoinPage, setIsJoinPage] = useState<boolean>(false);
  const ringCentralStatusRef = useRef<ConnectionStatusEnum>(ConnectionStatusEnum.DISCONNECTED);
  const isJoinPageRef = useRef<boolean>(false);
  const redirectUriMobile = `${window.location.protocol}//${window.location.host}/deeplink/ring-central/callback`;
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (isJoinPage && ringCentralServer) {
      (async () => {
        try {
          const response = await ringCentralApi.getRingCentral();
          let clientId = response?.data?.data?.clientId;
          let clientSecret = response?.data?.data?.clientSecret;

          if (!clientId || !clientSecret) return;
          clientId = decode(clientId);
          clientSecret = decode(clientSecret);
          handleLoadScript({
            clientId: clientId,
            clientSecret: clientSecret,
            redirectUri: `${ringCentralEmbeddableUrl}/redirect.html`,
          });
        } catch (e: any) {
          catchError(e, (message) => {
            notify('error', message);
          });
        }
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJoinPage]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    const clientId = localStorage.getItem(StorageEnum.CLIENT_ID);
    const clientSecret = localStorage.getItem(StorageEnum.CLIENT_SECRET);
    if (clientId && clientSecret) {
      handleLoadScript({
        clientId: decode(clientId),
        clientSecret: decode(clientSecret),
        redirectUri: redirectUriMobile,
      });
    }

    return () => {
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    ringCentralStatusRef.current = ringCentralStatus;
    isJoinPageRef.current = isJoinPage;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ringCentralStatus, isJoinPage]);
  //#endregion Implement Hook

  //#region Handle Function
  const postMessage = (message: Record<string, any>) => {
    const frame = document.querySelector<HTMLIFrameElement>(RING_CENTRAL_TYPE_WIDGET_FRAME_ADAPTER);
    frame?.contentWindow?.postMessage(message, '*');
  };

  const handleLoadScript = (data: { clientId?: string; clientSecret?: string; redirectUri?: string }) => {
    const { clientId, clientSecret, redirectUri } = data;
    let scriptUrl = `${ringCentralEmbeddableUrl}/adapter.js?appServer=${ringCentralServer}`;
    if (clientId) {
      scriptUrl += `&clientId=${clientId}`;
    }

    if (clientSecret) {
      scriptUrl += `&clientSecret=${clientSecret}`;
    }

    if (redirectUri) {
      scriptUrl += `&redirectUri=${redirectUri}`;
    }

    const script = document.createElement('script');
    script.src = scriptUrl;
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
  };

  const handleMessage = (event: MessageEvent) => {
    const data = event.data;
    if (!data) return;

    (window as any)?.ReactNativeWebView?.postMessage(JSON.stringify(data));
    switch (data.type) {
      case RING_CENTRAL_TYPE_ACTIVE_CALL_NOTIFY:
        if (data.call.telephonyStatus === RING_CENTRAL_TYPE_RINGING_TELEPHONY_STATUS && isJoinPageRef.current) {
          handleShowWidget(true);
          return;
        }
        break;
      case RING_CENTRAL_TYPE_CALL_END_NOTIFY:
        // Do nothing
        break;
      case RING_CENTRAL_TYPE_NATIVE_SEND_CREDENTIAL: {
        const clientId = data?.data?.clientId;
        const clientSecret = data?.data?.clientSecret;
        if (clientId && clientSecret) {
          localStorage.setItem(StorageEnum.CLIENT_ID, clientId);
          localStorage.setItem(StorageEnum.CLIENT_SECRET, clientSecret);
          handleLoadScript({
            clientId: decode(clientId),
            clientSecret: decode(clientSecret),
            redirectUri: redirectUriMobile,
          });
        }
        break;
      }
      case RING_CENTRAL_TYPE_NATIVE_AUTHORIZE: {
        const authCode = data?.data?.authorizeCode;
        if (authCode) {
          authorize(authCode);
        }
        break;
      }
      case RING_CENTRAL_TYPE_NATIVE_DISCONNECT:
        localStorage.removeItem(StorageEnum.CLIENT_ID);
        localStorage.removeItem(StorageEnum.CLIENT_SECRET);
        handleLogout();
        break;
      case RING_CENTRAL_TYPE_NATIVE_PHONE_CALL: {
        const phoneCallNumber = data?.data?.phoneNumber;
        if (phoneCallNumber) {
          handleCall(phoneCallNumber);
        }
        break;
      }
      case RING_CENTRAL_TYPE_NATIVE_SMS: {
        const phoneSMSNumber = data?.data?.phoneNumber;
        if (phoneSMSNumber) {
          handleSendSMS(phoneSMSNumber);
        }
        break;
      }
      case RING_CENTRAL_TYPE_CONNECTION_STATUS_NOTIFY:
        handleShowWidget(false);
        switch (data?.connectionStatus) {
          case RING_CENTRAL_TYPE_CONNECTED_STATUS:
            setRingCentralStatus(ConnectionStatusEnum.CONNECTED);
            break;
          case RING_CENTRAL_TYPE_CONNECTING_STATUS:
            setRingCentralStatus(ConnectionStatusEnum.CONNECTING);
            break;
          case RING_CENTRAL_TYPE_DISCONNECTED_STATUS:
            setRingCentralStatus(ConnectionStatusEnum.DISCONNECTED);
            break;
        }
        break;
      case RING_CENTRAL_TYPE_LOGIN_STATUS_NOTIFY:
        setRingCentralStatus(data?.loggedIn ? ConnectionStatusEnum.CONNECTING : ConnectionStatusEnum.DISCONNECTED);
        break;
      default:
        break;
    }
  };

  const handleCall = (phoneNumber: string) => {
    if (ringCentralStatusRef.current !== ConnectionStatusEnum.CONNECTED) return;

    handleShowWidget(true);
    postMessage({
      type: RING_CENTRAL_TYPE_NEW_CALL_ADAPTER,
      phoneNumber: phoneNumber,
      toCall: true,
    });
  };

  const handleLogin = () => {
    if (!ringCentralEmbeddableUrl || !ringCentralServer) {
      notify('error', 'Ring Central are not configured.');
      return;
    }

    const frame = document.querySelector<HTMLIFrameElement>(RING_CENTRAL_TYPE_WIDGET_FRAME_ADAPTER);

    if (!frame) {
      notify('error', 'Ring Central widget is not loaded. Please check your RingCentral credentials.');
      return;
    }

    handleShowWidget(true);
  };

  const handleLogout = () => {
    if (ringCentralStatusRef.current !== ConnectionStatusEnum.CONNECTED) return;

    postMessage({
      type: RING_CENTRAL_TYPE_LOGOUT_ADAPTER,
    });
  };

  const handleSendSMS = (phoneNumber: string) => {
    if (ringCentralStatusRef.current !== ConnectionStatusEnum.CONNECTED) return;

    handleShowWidget(true);
    postMessage({
      type: RING_CENTRAL_TYPE_NEW_SMS_ADAPTER,
      phoneNumber: phoneNumber,
      conversation: true,
    });
  };

  const handleShowWidget = (isShowWidget: boolean) => {
    (window as any)?.RCAdapter?.setClosed(!isShowWidget);
    (window as any)?.RCAdapter?.setMinimized(!isShowWidget);
  };

  const getAuthUrl = (clientId: string, redirectUri: string) => {
    return `${ringCentralServer}/restapi/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code`;
  };

  const authorize = (code: string) => {
    postMessage({
      type: RING_CENTRAL_TYPE_AUTHORIZATION_CODE_ADAPTER,
      callbackUri: `${ringCentralEmbeddableUrl}/redirect.html?code=${code}`,
    });
  };
  //#endregion Handle Function

  return (
    <RingCentralContext.Provider
      value={{
        ringCentralStatus,
        connect: handleLogin,
        disconnect: handleLogout,
        excuteCall: handleCall,
        sendSMS: handleSendSMS,
        setIsJoinPage: setIsJoinPage,
        setIsShowWidget: handleShowWidget,
        getAuthUrl: getAuthUrl,
        authorize: authorize,
      }}
    >
      {children}
    </RingCentralContext.Provider>
  );
};

export { RingCentralContext, RingCentralProvider };
